import React, { useState, useRef } from 'react';
import { Image as ImageIcon, Download, Upload } from 'lucide-react';
import './ToolsCommon.css';

const ImageCompressor = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [quality, setQuality] = useState(0.8);
  const [fileName, setFileName] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFileName(file.name);
      setOriginalSize(file.size);

      const reader = new FileReader();
      reader.onload = (event) => {
        setOriginalImage(event.target.result);
        compressImage(event.target.result, file.type);
      };
      reader.readAsDataURL(file);
    }
  };

  const compressImage = (imageSrc, mimeType) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      canvas.toBlob(
        (blob) => {
          setCompressedSize(blob.size);
          const reader = new FileReader();
          reader.onload = (e) => {
            setCompressedImage(e.target.result);
          };
          reader.readAsDataURL(blob);
        },
        mimeType,
        quality
      );
    };
    img.src = imageSrc;
  };

  const downloadCompressed = () => {
    const link = document.createElement('a');
    link.download = `compressed_${fileName}`;
    link.href = compressedImage;
    link.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const compressionPercent = originalSize > 0
    ? Math.round(((originalSize - compressedSize) / originalSize) * 100)
    : 0;

  return (
    <div className="tool-container">
      <div className="tool-content-wrapper">
        <div className="tool-header">
          <ImageIcon size={48} className="tool-icon" />
          <h1 className="tool-title">Image Compressor</h1>
          <p className="tool-subtitle">Reduce image file size without losing quality</p>
        </div>

        <div className="tool-panel">
          <div className="tool-section">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="tool-button primary"
              style={{ width: '100%' }}
            >
              <Upload size={20} />
              Select Image
            </button>
          </div>

          {originalImage && (
            <>
              <div className="tool-section">
                <label className="tool-label">Compression Quality: {Math.round(quality * 100)}%</label>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={quality}
                  onChange={(e) => {
                    setQuality(Number(e.target.value));
                    if (originalImage) {
                      compressImage(originalImage, 'image/jpeg');
                    }
                  }}
                  className="tool-range"
                />
              </div>

              <div className="file-info">
                <div className="file-info-item">
                  <div className="file-info-label">Original Size</div>
                  <div className="file-info-value">{formatFileSize(originalSize)}</div>
                </div>
                <div className="file-info-item">
                  <div className="file-info-label">Compressed Size</div>
                  <div className="file-info-value">{formatFileSize(compressedSize)}</div>
                </div>
                <div className="file-info-item">
                  <div className="file-info-label">Saved</div>
                  <div className="file-info-value" style={{ color: '#10b981' }}>
                    {compressionPercent}%
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginTop: '24px' }}>
                <div>
                  <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Original
                  </h3>
                  <img src={originalImage} alt="Original" className="image-preview" />
                </div>
                <div>
                  <h3 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                    Compressed
                  </h3>
                  <img src={compressedImage} alt="Compressed" className="image-preview" />
                </div>
              </div>

              <div className="tool-actions">
                <button onClick={downloadCompressed} className="tool-button primary">
                  <Download size={20} />
                  Download Compressed Image
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageCompressor;
